import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Divider, Button, Tooltip, Popconfirm } from 'antd';
import { debounce } from 'lodash';
import { DataCrafterTemplateFormStore, DataCrafterOutputVariantsStore } from '../../stores';
import { CreateEditOutputVariantDialog, InputPackageSelector, OutputPackageSelector, OutputVariantsSelector } from '..';
import './PackageSelectors.less';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { INITIAL_VARIANT_ID } from '../OutputVariantsSelector/OutputVariantsSelector';

interface Props {
    formStore: DataCrafterTemplateFormStore;
    outputVariantsStore: DataCrafterOutputVariantsStore;
}

const PackageSelectors: React.FC<Props> = ({ formStore, outputVariantsStore }) => {
    const debouncedFetchInputColumns = React.useMemo(
        () =>
            debounce(async () => {
                formStore.fetchInputColumns();
            }, 500),
        [formStore]
    );

    const handleInputPackagesChange = (values: string[]) => {
        formStore.setSelectedInputPackages(values);
        formStore.setFormChanged(true);
        debouncedFetchInputColumns();
    };

    const handleOutputPackageChange = (value: string | null) => {
        formStore.setSelectedOutputPackage(value);
        formStore.setFormChanged(true);

        if (formStore.selectedOutputPackage) {
            formStore.fetchOutputColumns();
        }
    };

    const handleOutputVariantChange = (variantId: string | undefined) => {
        if (variantId === INITIAL_VARIANT_ID) {
            outputVariantsStore.setSelectedOutputVariant(null);
            return;
        }

        const selectedVariant = variantId
            ? outputVariantsStore.outputVariants.find(variant => variant.variantId === variantId) || null
            : null;
        outputVariantsStore.setSelectedOutputVariant(selectedVariant);
    };

    const handleAddOutputVariant = () => {
        outputVariantsStore.setSelectedOutputVariant(null);
        outputVariantsStore.setOutputVariantDialogVisible(true);
        formStore.setFormChanged(true);
    };

    const handleEditOutputVariant = () => {
        outputVariantsStore.openEditOutputVariantDialog(outputVariantsStore.selectedOutputVariant!);
    };

    const handleDeleteOutputVariant = () => {
        outputVariantsStore.deleteOutputVariantLocal(outputVariantsStore.selectedOutputVariant!.variantId!);
        outputVariantsStore.setSelectedOutputVariant(null);
    };

    return (
        <div className="package-selectors-container">
            <div className="input-package-selector-container">
                <Form.Item label="Input Packages">
                    <InputPackageSelector
                        packages={formStore.packages}
                        isLoadingPackages={formStore.isLoadingPackages}
                        value={formStore.selectedInputPackages}
                        onChange={handleInputPackagesChange}
                        placeholder="Select input packages"
                        disabled={formStore.isBusyWithScriptOperation}
                    />
                </Form.Item>
            </div>

            <Divider className="package-selectors-divider" type="vertical" />

            <div className="output-package-selector-container">
                <Form.Item label="Output Packages">
                    <OutputPackageSelector
                        packages={formStore.packages}
                        isLoadingPackages={formStore.isLoadingPackages}
                        value={formStore.selectedOutputPackage}
                        onChange={handleOutputPackageChange}
                        placeholder="Select output package"
                        disabled={formStore.isBusyWithScriptOperation}
                    />
                </Form.Item>
            </div>
            <CreateEditOutputVariantDialog formStore={formStore} outputVariantsStore={outputVariantsStore} />

            {formStore.isEditMode && (
                <>
                    <Divider className="package-selectors-divider" type="vertical" />
                    <div className="output-variants-selector-container">
                        <Form.Item label="Output Variants">
                            <OutputVariantsSelector
                                variants={outputVariantsStore.outputVariants}
                                disabled={formStore.isBusyWithScriptOperation}
                                value={outputVariantsStore.selectedOutputVariant?.variantId}
                                onChange={handleOutputVariantChange}
                                placeholder="Select output variant"
                                isLoadingVariants={formStore.isCurrentTemplateLoading}
                            />
                            <div className="output-variants-selector-actions">
                                <Tooltip title="Add Output Variant">
                                    <Button
                                        size="middle"
                                        icon={<PlusOutlined />}
                                        type="link"
                                        onClick={handleAddOutputVariant}
                                        disabled={formStore.isBusyWithScriptOperation}
                                    />
                                </Tooltip>

                                {outputVariantsStore.selectedOutputVariant && (
                                    <>
                                        <Tooltip title="Edit Output Variant">
                                            <Button
                                                size="middle"
                                                icon={<EditOutlined />}
                                                type="link"
                                                onClick={handleEditOutputVariant}
                                                disabled={formStore.isBusyWithScriptOperation}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Delete Output Variant">
                                            <Popconfirm
                                                title="Are you sure you want to delete this output variant?"
                                                onConfirm={handleDeleteOutputVariant}
                                                okText="Delete"
                                                cancelText="Cancel"
                                                disabled={formStore.isBusyWithScriptOperation}
                                            >
                                                <Button
                                                    size="middle"
                                                    icon={<DeleteOutlined />}
                                                    type="link"
                                                    disabled={formStore.isBusyWithScriptOperation}
                                                />
                                            </Popconfirm>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        </Form.Item>
                    </div>
                </>
            )}
        </div>
    );
};

export default observer(PackageSelectors);
